import { Delete, KeyboardArrowDown } from '@mui/icons-material';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteEvent } from '~/api/events/delete';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { NetworkEventTypeEvents__EventType as EventType } from '../../queries/events.generated';

export interface BulkActionsProps {
  eventType: EventType;
  selectedIds: GridRowSelectionModel;
}

export const BulkActions = ({ eventType, selectedIds }: BulkActionsProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteEvent] = useDeleteEvent();

  if (!selectedIds.length) return null;

  const count = selectedIds.length;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        ref={ref}
        sx={(theme) => ({
          border: 1,
          borderColor: 'borders.light',
          color: theme.palette.primary.dark,
          fontWeight: 500,
          justifyContent: 'space-between',
          width: 200,
          '&, &:hover': {
            background: theme.palette.common.white,
          },
        })}
        variant="outlined"
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </Button>

      <Menu anchorEl={ref.current} open={open} onClose={() => setOpen(false)}>
        <MenuItem
          onClick={async () => {
            setOpen(false);
            if (!(await confirmDelete())) return;
            await deleteEvent({
              variables: { ids: selectedIds.map((id) => +id) },
            });
          }}
          sx={{ width: 200 }}
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText
            primary={t('pages.event_types.events_tab.bulk_actions.delete_events')}
            primaryTypographyProps={{ color: 'error' }}
          />
        </MenuItem>
      </Menu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.event_types.events_tab.bulk_actions.delete_confirm_button')}
        deleteConfirm
        prompt={
          <span
            dangerouslySetInnerHTML={{
              __html: t('pages.event_types.events_tab.bulk_actions.delete_confirm_prompt', {
                count,
                events: t('pages.event_types.events_tab.bulk_actions.event_plural', { count }),
                eventTypeName: eventType.name,
              }),
            }}
          />
        }
        title={t('pages.event_types.events_tab.bulk_actions.delete_confirm_title')}
      />
    </>
  );
};
